import React, { useEffect, useRef } from 'react';
import type { SvgIconProps } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import SearchIcon from '@mui/icons-material/Search';
import TagIcon from '@mui/icons-material/Tag';
import { AsOf, ParsedQuery, Phase, RecentScreen } from './types';
import { Jurisdiction, ScreeningProfile, coverage } from './profiles';
import { rangeLabel, riskCountryCode } from './utils';
import { TicketControl } from './SaveTicket';
import { RecentScreenRow } from './RecentScreens';
import {
  CountryPop,
  QueryRow,
  RailChip,
  RailSeg,
  RecentsMenu,
  RunActions,
  recentMenuItems,
} from './ComposerParts';

export interface QueryControl {
  inputRef: React.RefObject<HTMLInputElement>;
  value: string;
  onChange: (v: string) => void;
  parsed: ParsedQuery;
  recents: RecentScreen[];
  onPickRecent: (r: RecentScreen) => void;
}

export interface RouteControl {
  profile: ScreeningProfile;
  jurisdiction: Jurisdiction;
  onJurisdiction: (j: Jurisdiction) => void;
  partner: string;
  onPartner: (v: string) => void;
}

export interface OptionsControl {
  asOf: AsOf;
  onAsOf: (v: AsOf) => void;
  archived: boolean;
  onToggleArchived: () => void;
}

export interface RunControl {
  phase: Phase;
  stale: 'query' | 'options' | null;
  onRevert: () => void;
  canRun: boolean;
  cannotRunWhy: string;
  onRun: () => void;
  statusLine: string;
}

export type Popover = 'recents' | 'partner' | 'asof' | 'save' | 'scope';

interface ComposerProps {
  query: QueryControl;
  route: RouteControl;
  options: OptionsControl;
  run: RunControl;
  ticket: TicketControl;
  disabled: boolean;
  openPop: Popover | null;
  setOpenPop: (p: Popover | null) => void;
}

const TOKEN: Record<
  ParsedQuery['kind'],
  { className: string; glyph: React.ComponentType<SvgIconProps> }
> = {
  empty: { className: '', glyph: SearchIcon },
  hts: { className: 'psc-token--hts', glyph: TagIcon },
  badhts: { className: 'psc-token--bad', glyph: TagIcon },
  desc: { className: 'psc-token--desc', glyph: Inventory2OutlinedIcon },
};

const tokenLabel = (parsed: ParsedQuery): string =>
  parsed.kind === 'hts'
    ? `HTS ${parsed.fmt}`
    : parsed.kind === 'badhts'
    ? `${parsed.digits} digits — HTS codes have 6, 8 or 10`
    : 'Description';

const Composer: React.FC<ComposerProps> = ({
  query,
  route,
  options,
  run,
  ticket,
  disabled,
  openPop,
  setOpenPop,
}) => {
  const { parsed } = query;
  const { profile } = route;
  const partnerKey = profile.partner.key;
  const fixedOrigin = partnerKey === 'destination';
  const isRun = run.phase === 'run';
  const isDone = run.phase === 'done';
  const shellRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!openPop || openPop === 'scope') {
      return undefined;
    }
    const onDown = (e: MouseEvent) => {
      if (shellRef.current && !shellRef.current.contains(e.target as Node)) {
        setOpenPop(null);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpenPop(null);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [openPop, setOpenPop]);

  const restoringFocus = useRef(false);
  const triggers = useRef<Partial<Record<Popover, HTMLElement | null>>>({});
  const lastPop = useRef<Popover | null>(openPop);
  useEffect(() => {
    const closed = lastPop.current;
    lastPop.current = openPop;
    if (!closed || closed === 'scope' || openPop) {
      return;
    }
    const active = document.activeElement;
    const focusWasLost =
      !active ||
      active === document.body ||
      Boolean(shellRef.current?.contains(active));
    if (focusWasLost) {
      restoringFocus.current = true;
      triggers.current[closed]?.focus();
      restoringFocus.current = false;
    }
  }, [openPop]);
  const triggerRef = (name: Popover) => (el: HTMLElement | null) => {
    triggers.current[name] = el;
  };

  const toggle = (name: Popover) => setOpenPop(openPop === name ? null : name);

  const pickPartner = (v: string) => {
    route.onPartner(v);
    setOpenPop(null);
  };

  const isCode = parsed.kind === 'hts' || parsed.kind === 'badhts';

  const menuRef = useRef<HTMLDivElement | null>(null);
  const offersRecents = isDone && query.recents.length > 0;
  const showRecents = (value: string) => {
    if (offersRecents && value === '') {
      setOpenPop('recents');
    } else if (openPop === 'recents') {
      setOpenPop(null);
    }
  };
  const clearField = () => {
    query.onChange('');
    query.inputRef.current?.focus();
    showRecents('');
  };

  return (
    <div className='psc-shell' ref={shellRef}>
      <QueryRow
        glyph={TOKEN[parsed.kind].glyph}
        inputId='psc-q'
        label='HTS code or product description'
        placeholder='HTS code or product description'
        value={query.value}
        onChange={(v) => {
          query.onChange(v);
          showRecents(v);
        }}
        inputRef={(el) => {
          (
            query.inputRef as React.MutableRefObject<HTMLInputElement | null>
          ).current = el;
          triggers.current.recents = el;
        }}
        onFocus={() => {
          if (!restoringFocus.current) {
            showRecents(query.value);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && run.canRun) {
            run.onRun();
          } else if (e.key === 'Escape' && run.stale === 'query') {
            e.preventDefault();
            run.onRevert();
          } else if (e.key === 'ArrowDown' && openPop === 'recents') {
            e.preventDefault();
            recentMenuItems(menuRef.current)[0]?.focus();
          }
        }}
        onClear={clearField}
        disabled={disabled}
        isCode={isCode}
        describedBy={parsed.kind !== 'empty' ? 'psc-token' : undefined}
        expanded={offersRecents ? openPop === 'recents' : undefined}
        controls={openPop === 'recents' ? 'psc-recents-menu' : undefined}
        token={
          parsed.kind !== 'empty' && (
            <span
              id='psc-token'
              className={`psc-token ${TOKEN[parsed.kind].className}`}
              aria-live='polite'
            >
              {tokenLabel(parsed)}
            </span>
          )
        }
        actions={
          <RunActions
            phase={run.phase}
            canRun={run.canRun}
            cannotRunWhy={run.cannotRunWhy}
            onRun={run.onRun}
            newQuery={run.stale === 'query'}
            ticket={{
              ...ticket,
              open: openPop === 'save',
              triggerRef: triggerRef('save'),
              onToggle: () => toggle('save'),
              onClose: () => setOpenPop(null),
            }}
          />
        }
        menu={
          openPop === 'recents' && (
            <RecentsMenu
              id='psc-recents-menu'
              menuRef={menuRef}
              onExitTop={() => query.inputRef.current?.focus()}
            >
              {query.recents.map((r) => (
                <RecentScreenRow
                  key={`${r.q}-${r.at}`}
                  profile={profile}
                  screen={r}
                  onPick={(picked) => {
                    setOpenPop(null);
                    query.onPickRecent(picked);
                  }}
                  className='psc-recent--menu'
                />
              ))}
            </RecentsMenu>
          )
        }
      />

      <div className='psc-rail'>
        {profile.jurisdictions.length > 1 ? (
          <RailSeg
            label={`${profile.direction} jurisdiction`}
            options={profile.jurisdictions.map((j) => ({
              id: j.id,
              label: j.label,
            }))}
            value={route.jurisdiction.id}
            onChange={(id) => {
              const next = profile.jurisdictions.find((j) => j.id === id);
              if (next) {
                route.onJurisdiction(next);
              }
            }}
            disabled={disabled || isRun}
          />
        ) : (
          <span
            className='psc-fixed'
            title={`${profile.direction} screening covers ${coverage(
              profile,
              route.jurisdiction,
            )}`}
          >
            From<span className='v'>{route.jurisdiction.region}</span>
          </span>
        )}
        {fixedOrigin ? (
          <span className='psc-route-arrow' aria-hidden='true'>
            <ArrowForwardIcon sx={{ fontSize: 14 }} />
          </span>
        ) : (
          <span className='psc-vsep' />
        )}

        <div className='psc-pos'>
          <RailChip
            label={profile.partner.label}
            value={route.partner || 'Not set'}
            warn={!route.partner}
            triggerRef={triggerRef('partner')}
            onClick={() => toggle('partner')}
            expanded={openPop === 'partner'}
            disabled={disabled}
            title={
              !route.partner
                ? 'Matches cover every country. The AI evaluation needs a country.'
                : riskCountryCode(route.partner)
                ? `Matches cover ${route.partner} and lists that apply to every country`
                : 'Matches cover every country'
            }
          />
          {openPop === 'partner' && (
            <CountryPop
              inputId='psc-os'
              dialogLabel={`Country of ${partnerKey}`}
              searchPlaceholder={profile.partner.search}
              value={route.partner}
              noneLabel={`No ${partnerKey}`}
              note={`Matches cover the country of ${partnerKey} plus lists that apply to every country. With no ${partnerKey}, they cover every country, and the AI evaluation needs one.`}
              onPick={pickPartner}
            />
          )}
        </div>
        {fixedOrigin && <span className='psc-vsep' />}

        <div className='psc-pos'>
          <RailChip
            label='As of'
            value={rangeLabel(options.asOf)}
            tnum
            set={options.asOf.mode === 'range'}
            triggerRef={triggerRef('asof')}
            onClick={() => toggle('asof')}
            expanded={openPop === 'asof'}
            disabled={disabled}
          />
          {openPop === 'asof' && (
            <div
              className='psc-pop psc-pop--asof'
              role='dialog'
              aria-label='As of'
            >
              <div
                className='psc-radios'
                role='radiogroup'
                aria-label='Which records to screen'
              >
                {[
                  ['today', 'Today — current lists'],
                  ['range', 'A date range'],
                ].map(([v, label]) => (
                  <label
                    key={v}
                    className={`psc-opt psc-radio${
                      options.asOf.mode === v ? ' is-on' : ''
                    }`}
                  >
                    <input
                      type='radio'
                      name='psc-asof'
                      checked={options.asOf.mode === v}
                      onChange={() =>
                        options.onAsOf({
                          ...options.asOf,
                          mode: v as AsOf['mode'],
                        })
                      }
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
              {options.asOf.mode === 'range' && (
                <div className='psc-dates'>
                  <label className='psc-label'>
                    FROM
                    <input
                      type='date'
                      className='psc-field'
                      value={options.asOf.from}
                      max={options.asOf.to || undefined}
                      onChange={(e) =>
                        options.onAsOf({
                          ...options.asOf,
                          from: e.target.value,
                        })
                      }
                    />
                  </label>
                  <label className='psc-label'>
                    TO
                    <input
                      type='date'
                      className='psc-field'
                      value={options.asOf.to}
                      min={options.asOf.from || undefined}
                      onChange={(e) =>
                        options.onAsOf({ ...options.asOf, to: e.target.value })
                      }
                    />
                  </label>
                </div>
              )}
              <div className='psc-pop-note'>
                Limits matches to records updated on BITE within the range.
              </div>
            </div>
          )}
        </div>

        <RailChip
          label='Archived data'
          value={options.archived ? 'On' : 'Off'}
          set={options.archived}
          onClick={options.onToggleArchived}
          pressed={options.archived}
          disabled={disabled}
          title='Archived data surfaces records that have been removed from active data sets, for historical reference.'
        />

        <span className='psc-status' role='status'>
          {run.statusLine}
        </span>
      </div>
    </div>
  );
};

export default Composer;
